/* Preview + render surface for the 30-second commercial.

   /ad            — review it in the browser (square by default)
   /ad?ar=9x16    — the Reels/Stories cut
   /ad?ar=16x9    — the widescreen cut
   /ad?render=1   — chrome-free stage, clock driven by the renderer (window.__adT)

   This page is what scripts/render_commercial.py films, so anything that is not the film itself
   lives OUTSIDE [data-testid=ad-stage]. */
import { useEffect, useMemo, useRef, useState } from "react";
import AdFilm from "@/components/ad/AdFilm";
import { Play, Pause, RotateCcw, Download, Loader2 } from "lucide-react";

const AR = { "1x1": [1080, 1080], "9x16": [1080, 1920], "16x9": [1920, 1080] };
const AUDIO = "/ad/commercial.mp3";

export default function AdFilmPage() {
  const params = new URLSearchParams(window.location.search);
  const renderMode = params.get("render") === "1";
  const arKey = AR[params.get("ar")] ? params.get("ar") : "1x1";
  const [frameW, frameH] = AR[arKey];

  const stage = useRef(null);
  const audio = useRef(null);
  const raf = useRef(0);
  const [data, setData] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    fetch("/ad/script.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setData({ duration: 30, lines: [] }));
  }, []);

  /* Scene segments: each scene runs from its first line to the start of the next scene. */
  const segments = useMemo(() => {
    const lines = data?.lines || [];
    const out = [];
    lines.forEach((l) => {
      const last = out[out.length - 1];
      if (last && last.scene === l.scene) last.end = l.end;
      else out.push({ scene: l.scene, start: l.start, end: l.end });
    });
    out.forEach((s, i) => { s.end = i + 1 < out.length ? out[i + 1].start : (data?.duration || s.end); });
    if (out.length) out[0].start = 0;
    return out;
  }, [data]);

  const line = useMemo(() => {
    const hit = (data?.lines || []).filter((l) => t >= l.start && t <= l.end + 0.08).slice(-1)[0];
    return hit?.text || "";
  }, [data, t]);

  /* Fit the fixed-size film into whatever space the browser gives it. */
  useEffect(() => {
    const fit = () => {
      const pad = renderMode ? 0 : 48;
      const availW = window.innerWidth - pad;
      const availH = window.innerHeight - (renderMode ? 0 : 150);
      setScale(renderMode ? 1 : Math.min(availW / frameW, availH / frameH, 1));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [frameW, frameH, renderMode]);

  /* Playback clock. In render mode the renderer sets window.__adT and we stamp the body once we
     have painted that exact instant — the same handshake the tour render uses. */
  useEffect(() => {
    if (renderMode) return undefined;
    const loop = () => {
      raf.current = requestAnimationFrame(loop);
      const a = audio.current;
      if (a && !a.paused) setT(a.currentTime);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [renderMode]);

  useEffect(() => {
    if (!renderMode) return undefined;
    window.__AD_RENDER__ = true;
    const loop = () => {
      raf.current = requestAnimationFrame(loop);
      const want = window.__adT;
      if (typeof want === "number" && want !== t) setT(want);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [renderMode, t]);

  useEffect(() => {
    if (renderMode) document.body.dataset.adT = t.toFixed(4);
  }, [t, renderMode]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) {
      if (a.currentTime >= (data?.duration || 30) - 0.1) a.currentTime = 0;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const restart = () => {
    const a = audio.current;
    if (!a) return;
    a.currentTime = 0;
    setT(0);
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B1330" }}>
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="ad-page"
      className={`min-h-screen flex flex-col items-center ${renderMode ? "" : "justify-center gap-5 p-6"}`}
      style={{ background: renderMode ? "#0B1330" : "#070c1e" }}>
      <div style={{ width: frameW * scale, height: frameH * scale }}>
        <div ref={stage} data-testid="ad-stage"
          style={{ width: frameW, height: frameH, position: "relative", overflow: "hidden",
                   transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <AdFilm t={t} segments={segments} line={line} w={frameW} h={frameH} />
        </div>
      </div>

      {!renderMode && (
        <>
          <audio ref={audio} src={AUDIO} preload="auto" playsInline data-testid="ad-audio"
            onEnded={() => setPlaying(false)} className="hidden" />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button data-testid="ad-play-btn" onClick={toggle}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-gold px-6 font-display
                         text-xs font-bold uppercase tracking-[0.16em] text-royal-deep">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {playing ? "Pause" : "Play the commercial"}
            </button>
            <button data-testid="ad-restart-btn" onClick={restart}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-white/25
                         px-5 text-xs font-bold uppercase tracking-[0.16em] text-white">
              <RotateCcw className="h-3.5 w-3.5" /> Restart
            </button>
            {Object.keys(AR).map((k) => (
              <a key={k} href={`/ad?ar=${k}`} data-testid={`ad-ar-${k}`}
                className={`inline-flex h-11 items-center rounded-full px-5 text-xs font-bold
                            uppercase tracking-[0.16em] ${arKey === k
                              ? "bg-white/15 text-white" : "text-blue-100/60 hover:text-white"}`}>
                {k === "1x1" ? "Square" : k === "9x16" ? "Vertical" : "Widescreen"}
              </a>
            ))}
            <a href="/api/public/ad/square/download" data-testid="ad-download-btn"
              className="inline-flex h-11 items-center gap-2 rounded-full border border-gold/60
                         px-5 text-xs font-bold uppercase tracking-[0.16em] text-gold">
              <Download className="h-3.5 w-3.5" /> Download MP4
            </a>
          </div>
          <p className="text-[11px] text-blue-100/50">
            Live preview · {data.duration?.toFixed?.(1) || 30}s · {t.toFixed(1)}s
          </p>
        </>
      )}
    </div>
  );
}
